import { nanoid } from 'nanoid';

import { CartItem } from '@sola/schema/dotcom/cart/cart-item.ts';
import { Cart } from '@sola/schema/dotcom/cart/cart.ts';
import { CommitmentFrequencyEnum } from '@sola/schema/dotcom/cart/commitments.ts';
import {
  CartErrorTypeEnum,
  HoldLostBase,
} from '@sola/schema/dotcom/cart/errors.ts';
import { ChildAvailabilityItem } from '@sola/schema/dotcom/children/child.ts';
import { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';

import {
  isItemGift,
  isItemGiftBirthday,
  isItemGiftCenter,
} from '../cart/cart-item-type-guards.ts';
import { UserError } from '../errors-user.ts';

export const userErrorCartGeneric = (
  args: Omit<Partial<UserError>, 'type'> = {},
) => {
  const { id, statusCode, ...rest } = args;
  return new UserError({
    type: CartErrorTypeEnum.CartGeneric,
    data: { ...rest },
    ...(id && { id }),
    ...(statusCode && { statusCode }),
  });
};

export const userErrorCartItemsAdd = () => {
  return new UserError({
    type: CartErrorTypeEnum.ItemsAdd,
    statusCode: 500,
  });
};

export const userErrorCartEmpty = () =>
  new UserError({
    type: CartErrorTypeEnum.CartEmpty,
  });

// TODO :: this cannot return generic error
export const userErrorCartMaxExceeded = (data: {
  cartId: Cart['id'];
  maxLimit: number;
  total: number;
}) =>
  new UserError({
    type: CartErrorTypeEnum.CartMaxExceeded,
    userShouldFix: true,
    data,
  });

export const userErrorCartSave = (data: {
  cartId: Cart['id'];
  supporterId: Cart['supporterId'];
}) => userErrorCartGeneric({ data });

export const userErrorCartInactiveFund = () =>
  new UserError({
    type: CartErrorTypeEnum.InactiveFund,
  });

export const userErrorCartIncompleteFundData = (data: {
  missingProps: string;
}) =>
  new UserError({
    // TODO: This isn't really an inactive fund...
    type: CartErrorTypeEnum.InactiveFund,
    ...(data && { data }),
  });

export const userErrorCartItemAmountRange = (data: {
  id: string;
  amount: CartItem['amount'];
  // cartItem: CartItem;
  min: number;
  max: number;
}) => {
  const { id, ...rest } = data;
  return new UserError({
    type: CartErrorTypeEnum.ItemAmountRange,
    id,
    data: rest,
    userShouldFix: true,
  });
};

export const userErrorCartItemExists = () => userErrorCartGeneric();
// If we need to display a custom item exists error
// export const userErrorCartItemExists = () => {
//   return new UserError({
//     type: CartErrorTypeEnum.ItemExists,
//     statusCode: 400,
//   });
// };

export const userErrorCartGiftUnavailable = (cartItem: CartItem) => {
  if (!isItemGift(cartItem)) {
    return;
  }
  return new UserError({
    type: CartErrorTypeEnum.GiftUnavailable,
    data: {
      childName: !isItemGiftCenter(cartItem)
        ? cartItem.child?.name
        : cartItem.gift.centerId,
      giftType: cartItem.gift.giftType.toLowerCase(),
    },
    statusCode: 410,
  });
};

export const userErrorCartGiftConflict = (cartItem: CartItem) => {
  return new UserError({
    type: isItemGiftBirthday(cartItem)
      ? CartErrorTypeEnum.GiftConflictBirthday
      : CartErrorTypeEnum.GiftConflictChild,
    data: {
      childName: cartItem.child?.name,
    },
    statusCode: 409,
  });
};

export const userErrorCartItemMaxExceeded = (data: {
  id: string;
  amount: CartItem['amount'];
  // cartItem: CartItem;
  maxLimit: number;
}) => {
  const { id, ...rest } = data;
  return new UserError({
    type: CartErrorTypeEnum.ItemMaxExceeded,
    id,
    data: rest,
    userShouldFix: true,
  });
};

export const userErrorCartItemMinSubceeded = (data: {
  id: string;
  amount: CartItem['amount'];
  // cartItem: CartItem;
  minLimit: number;
}) => {
  const { id, ...rest } = data;
  return new UserError({
    type: CartErrorTypeEnum.ItemMinSubceeded,
    id,
    data: rest,
    userShouldFix: true,
  });
};

export const userErrorCartItemUnavaliableFreq = (data: {
  frequency: CommitmentFrequencyEnum;
}) => userErrorCartGeneric({ data });

type ChildInfo = Pick<HoldLostBase, 'childPronoun'> & {
  childName: string;
};

export const userErrorCartHoldExtendFailed = () =>
  new UserError({
    type: CartErrorTypeEnum.HoldExtendFailed,
  });

export const userErrorCartHoldFailed = ({
  globalId,
  childName,
  sourceCode,
}: {
  globalId: string;
  childName?: string;
  sourceCode?: string;
}) =>
  new UserError({
    id: globalId ?? nanoid(),
    type: CartErrorTypeEnum.HoldFailed,
    statusCode: 400,
    data: {
      globalId,
      ...(childName && {
        childName,
      }),
      ...(sourceCode && {
        sourceCode,
      }),
    },
  });

export const userErrorCartMultiHoldFailed = (
  children?: ChildAvailabilityItem | ChildAvailabilityItem[],
) =>
  new UserError({
    type: CartErrorTypeEnum.HoldMultiFailed,
    // data: {
    //   childName: Array.isArray(children)
    //     ? children
    //         ?.map((child) => {
    //           return child.name.preferred;
    //         })
    //         .toString()
    //     : children?.name.preferred || `those children's`,
    //   // childPronoun: isMultipleChildren ? 'them' : childrenInfo[0].childPronoun,
    // },
  });

export const userErrorCartHoldLost = ({
  cartItems,
  failedHoldIds,
  statusCode,
}: {
  cartItems?: CartItem[];
  // Can be cart item ID or global ID
  failedHoldIds: string[];
  statusCode?: number;
}) => {
  if (!cartItems) {
    return new UserError({
      type: CartErrorTypeEnum.HoldLost,
      data: {},
      statusCode,
    });
  }

  const childrenInfo = cartItems
    .filter(
      (item) =>
        failedHoldIds.includes(item.id) ||
        failedHoldIds.includes(item.child?.globalId || ''),
    )
    .reduce((acc: ChildInfo[], cur): ChildInfo[] => {
      if (cur?.child?.name) {
        const childPronoun =
          cur.child.gender === ChildGenderEnum.Female ? 'her' : 'him';
        return [...acc, { childName: cur.child.name, childPronoun }];
      }

      return acc;
    }, []);

  // For multiple children, we should probably rethink this - the user doesn't know there are specific kids, we should just go grab new matching kids...
  // TODO :: if no `childNames`, we need to do something different here
  const isMultipleChildren = childrenInfo?.length > 1;

  return new UserError({
    type: CartErrorTypeEnum.HoldLost,
    // Keeping this in case we log it on the server.
    // logArgs.logLevel: Info,
    data: {
      childName:
        childrenInfo.length < 2
          ? childrenInfo[0]?.childName || 'that child'
          : childrenInfo.map((child) => child.childName).join(', '),
      childPronoun: isMultipleChildren ? 'them' : childrenInfo[0]?.childPronoun,
      holdIds: failedHoldIds,
    },
    statusCode,
  });
};

export const userErrorCartHoldSponsored = ({
  type,
  id,
  childName,
}: {
  type?: CartErrorTypeEnum;
  id?: string;
  childName?: string;
}) =>
  new UserError({
    type: type ?? CartErrorTypeEnum.HoldSponsored,
    ...(id && { id }),
    // TODO
    statusCode: 410,
    data: { childName: childName ?? 'the child that you chose' },
  });

export const userErrorCartConflict = () => {
  return new UserError({
    type: CartErrorTypeEnum.CartConflict,
    severity: 'warning',
    statusCode: 409,
  });
};

export const userErrorCartMergeLoser = () => {
  return new UserError({
    type: CartErrorTypeEnum.CartMergeLoser,
    severity: 'warning',
    statusCode: 409,
  });
};

export const userErrorCartPremiumFailed = () =>
  new UserError({ type: CartErrorTypeEnum.PremiumFail });
